import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  Flex,
  Image,
  useColorModeValue,
  Heading,
  useToast,
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Badge,
} from "@chakra-ui/react";
import {
  fetchCardDeliverables,
  approveDesign,
  declineDesign,
} from "../../../../ProjectBoardServices/BoardModalAPI/LeftModalAPI/DeliverablesTabAPI";

const DeliveryTab = ({ task, reloadKey }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const toast = useToast();
  const cancelRef = useRef();

  const tableBg = useColorModeValue("white", "gray.800");

  /* ── Row background by status ── */
  const getRowBgColor = (status) => {
    if (!status) return "transparent";
    const s = status.toLowerCase();
    if (s === "approved") return "#f0fdf4";
    if (s === "pending")  return "#fffbeb";
    if (s === "declined" || s === "rejected") return "#fef2f2";
    return "transparent";
  };

  /* ── Badge colour scheme ── */
  const getStatusColor = (status) => {
    if (!status) return "gray";
    const s = status.toLowerCase();
    if (s === "approved") return "green";
    if (s === "pending")  return "yellow";
    if (s === "declined" || s === "rejected") return "red";
    return "gray";
  };

  /* ── Load deliverables ── */
  const loadDeliverables = async () => {
    setLoading(true);
    try {
      const response = await fetchCardDeliverables(task?.id);
      if (response?.data?.status && response?.data?.data?.data) {
        setData(response.data.data.data);
      } else {
        setData([]);
        toast({ title: "No deliverables found", status: "info", duration: 3000 });
      }
    } catch (error) {
      console.error("Error fetching deliverables:", error);
      toast({
        title: "Error loading deliverables",
        description: error.message || "Unknown error",
        status: "error",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (task?.id) loadDeliverables();
  }, [task?.id, reloadKey]);

  const openDialog = (deliverable, type) => {
    setSelectedDeliverable(deliverable);
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const confirmAction = async () => {
    if (!selectedDeliverable || !dialogType || !task?.id) return;
    setLoadingAction(true);
    try {
      if (dialogType === "approve") {
        await approveDesign(task.id, selectedDeliverable.id);
        toast({
          title: "Approved!",
          description: `"${selectedDeliverable.inventory?.title || selectedDeliverable.title}" has been approved.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else if (dialogType === "decline") {
        await declineDesign(task.id, selectedDeliverable.id);
        toast({
          title: "Declined!",
          description: `"${selectedDeliverable.inventory?.title || selectedDeliverable.title}" has been declined.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
      setIsDialogOpen(false);
      setSelectedDeliverable(null);
      setDialogType("");
      loadDeliverables();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
    setLoadingAction(false);
  };

  /* ════════════════════════════════
     RENDER
  ════════════════════════════════ */
  return (
    <Box
      p={5}
      bg={tableBg}
      borderRadius="lg"
      shadow="sm"
      border="1px solid"
      borderColor="gray.100"
      maxW="1000px"
      mx="auto"
    >
      {/* ── Header ── */}
      <Heading as="h3" size="md" mb={3} color="blue.600" fontWeight="600">
        Deliverables
      </Heading>

      {/* ── Hint banner ── */}
      <Flex
        align="center"
        gap={2}
        mb={5}
        px={3}
        py={2}
        bg="orange.50"
        border="1px solid"
        borderColor="orange.200"
        borderRadius="md"
        w="fit-content"
      >
        <Text fontSize="xs" color="orange.600" fontWeight="500">
          ⚠ Latest file will be marked as Approval.
        </Text>
      </Flex>

      {/* ── States ── */}
      {loading ? (
        <Flex justify="center" align="center" h="200px" gap={3}>
          <Spinner size="sm" color="blue.500" />
          <Text fontSize="sm" color="gray.500">
            Loading deliverables...
          </Text>
        </Flex>
      ) : data.length === 0 ? (
        <Flex
          justify="center"
          align="center"
          h="160px"
          direction="column"
          gap={2}
          border="1px dashed"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Text fontSize="2xl">📂</Text>
          <Text fontSize="sm" color="gray.400">
            No deliverables found.
          </Text>
        </Flex>
      ) : (
        /* ── Table ── */
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          overflow="hidden"
        >
          <Box overflowX="auto">
            <Table
              size="sm"
              variant="simple"
              style={{ tableLayout: "fixed", width: "100%", minWidth: "700px" }}
            >
              <Thead bg="blue.500">
                <Tr>
                  <Th color="white" fontSize="xs" py={3} w="18%">
                    Title
                  </Th>
                  <Th color="white" fontSize="xs" py={3} w="13%">
                    Category
                  </Th>
                  <Th color="white" fontSize="xs" py={3} w="20%">
                    File Name
                  </Th>
                  <Th color="white" fontSize="xs" py={3} w="10%" textAlign="center">
                    Preview
                  </Th>
                  <Th color="white" fontSize="xs" py={3} w="11%" textAlign="center">
                    Status
                  </Th>
                  <Th color="white" fontSize="xs" py={3} textAlign="center">
                    Mark as Deliverable
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {data.map((deliverable, index) => {
                  const output      = deliverable.outputs;
                  const status      = deliverable.status;
                  const showActions = status?.toLowerCase() === "pending";

                  return (
                    <Tr
                      key={deliverable.id || index}
                      style={{
                        backgroundColor: getRowBgColor(status),
                        transition: "background 0.2s ease-in-out",
                      }}
                      _hover={{ filter: "brightness(0.97)" }}
                    >
                      {/* Title */}
                      <Td fontSize="xs" fontWeight="500" isTruncated>
                        {deliverable.inventory?.title || deliverable.title || "—"}
                      </Td>

                      {/* Category */}
                      <Td fontSize="xs" color="gray.600" isTruncated>
                        {deliverable.inventory?.category || deliverable.category || "—"}
                      </Td>

                      {/* File name */}
                      <Td fontSize="xs" color="gray.700" isTruncated>
                        {output?.original_name || "—"}
                      </Td>

                      {/* Preview */}
                      <Td textAlign="center">
                        {output?.file_type?.startsWith("image/") ? (
                          <Image
                            src={output.file_path}
                            alt={output.original_name}
                            boxSize="36px"
                            borderRadius="md"
                            objectFit="cover"
                            border="1px solid"
                            borderColor="gray.200"
                            mx="auto"
                          />
                        ) : (
                          <Badge
                            fontSize="0.6rem"
                            colorScheme="gray"
                            variant="outline"
                            px={2}
                            py={0.5}
                            borderRadius="sm"
                            letterSpacing="0.05em"
                          >
                            {output?.extension?.toUpperCase() || "N/A"}
                          </Badge>
                        )}
                      </Td>

                      {/* Status */}
                      <Td textAlign="center">
                        {status ? (
                          <Badge
                            colorScheme={getStatusColor(status)}
                            textTransform="capitalize"
                            fontSize="0.65rem"
                            px={2}
                            py={0.5}
                            borderRadius="full"
                          >
                            {status}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>

                      {/* Actions */}
                      <Td textAlign="center">
                        {showActions ? (
                          <Flex justify="center" gap={2} wrap="wrap">
                            <Button
                              size="xs"
                              colorScheme="green"
                              px={3}
                              onClick={() => openDialog(deliverable, "approve")}
                            >
                              Approve
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              px={3}
                              onClick={() => openDialog(deliverable, "decline")}
                            >
                              Decline
                            </Button>
                          </Flex>
                        ) : (
                          <Text fontSize="sm" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}

      {/* ── Confirm Dialog ── */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDialogOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="lg" mx={4}>
            <AlertDialogHeader
              fontSize="md"
              fontWeight="600"
              borderBottom="1px solid"
              borderColor="gray.100"
              pb={3}
            >
              {dialogType === "approve" ? "✅ Approve Deliverable" : "❌ Decline Deliverable"}
            </AlertDialogHeader>

            <AlertDialogBody py={5}>
              <Text fontSize="sm" color="gray.600">
                {dialogType === "approve"
                  ? "Are you sure you want to approve this deliverable?"
                  : "Are you sure you want to decline this deliverable?"}
              </Text>
              <Box
                mt={3}
                p={3}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text fontSize="sm" fontWeight="500" color="gray.800">
                  {selectedDeliverable?.inventory?.title ||
                    selectedDeliverable?.title ||
                    "Deliverable"}
                </Text>
              </Box>
            </AlertDialogBody>

            <AlertDialogFooter gap={2} borderTop="1px solid" borderColor="gray.100" pt={3}>
              <Button ref={cancelRef} size="sm" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme={dialogType === "approve" ? "green" : "red"}
                onClick={confirmAction}
                isLoading={loadingAction}
                px={5}
              >
                {dialogType === "approve" ? "Yes, Approve" : "Yes, Decline"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default DeliveryTab;